'use client';
import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const features = [
  {
    id: 'whitelabel',
    colSpan: 'lg:col-span-2',
    icon: 'DevicePhoneMobileIcon',
    number: '01',
    title: 'White-Label Mobile Ecosystem',
    description: 'Launch fully branded Android and iOS apps for customers, drivers, delivery partners, and fleet owners—under your own brand, with complete ownership of your customer relationships.',
    metric: 'Full Brand Ownership',
    metricLabel: 'Android & iOS apps',
    tag: 'Core Platform',
    highlight: true,
  },
  {
    id: 'dashboard',
    colSpan: 'lg:col-span-1',
    icon: 'CpuChipIcon',
    number: '02',
    title: 'Unified Operations Dashboard',
    description: 'Manage bookings, drivers, vehicles, dispatch, payments, support, and business performance from one powerful, real-time command center.',
    metric: '1 Dashboard',
    metricLabel: 'complete control',
    tag: 'Operations',
  },
  {
    id: 'omnichannel',
    colSpan: 'lg:col-span-1',
    icon: 'ChatBubbleLeftRightIcon',
    number: '03',
    title: 'Omnichannel Booking Experience',
    description: 'Accept bookings through mobile apps, websites, WhatsApp, call centers, and kiosk-based channels—ensuring customers can reach you anytime, anywhere.',
    metric: '5+ Channels',
    metricLabel: 'booking touchpoints',
    tag: 'Booking',
  },
  {
    id: 'ai',
    colSpan: 'lg:col-span-2',
    icon: 'BoltIcon',
    number: '04',
    title: 'AI-Powered Operational Intelligence',
    description: 'Optimize fleet utilization with intelligent driver allocation, dynamic routing, demand forecasting, surge management, and predictive operational insights.',
    metric: '40% efficiency gain',
    metricLabel: 'fleet utilization',
    tag: 'AI & Analytics',
  },
  {
    id: 'analytics',
    colSpan: 'lg:col-span-1',
    icon: 'ChartBarIcon',
    number: '05',
    title: 'Real-Time Business Analytics',
    description: 'Monitor trips, revenue, utilization, customer behavior, city-wise performance, and growth metrics with actionable dashboards designed for faster decision-making.',
    metric: 'Live Insights',
    metricLabel: 'real-time dashboards',
    tag: 'Analytics',
  },
  {
    id: 'multicity',
    colSpan: 'lg:col-span-1',
    icon: 'MapPinIcon',
    number: '06',
    title: 'Multi-City Franchise Expansion',
    description: 'Scale across cities, regions, and states with centralized control, localized operations, franchise management, and standardized business workflows.',
    metric: '22+ Cities',
    metricLabel: 'already operational',
    tag: 'Expansion',
  },
  {
    id: 'multivertical',
    colSpan: 'lg:col-span-1',
    icon: 'ArrowPathIcon',
    number: '07',
    title: 'Multi-Vertical Platform Expansion',
    description: 'Expand beyond ride-hailing into logistics, parcel delivery, food delivery, grocery, and hyperlocal services—using one scalable technology platform.',
    metric: '7+ Verticals',
    metricLabel: 'on one platform',
    tag: 'Multi-Vertical',
  },
  {
    id: 'local',
    colSpan: 'lg:col-span-1',
    icon: 'GlobeAltIcon',
    number: '08',
    title: 'Local Market Adaptability',
    description: 'Built for Tier 2, Tier 3, and Tier 4 markets with local language support, regional pricing flexibility, and operational workflows designed for Indian businesses.',
    metric: 'Tier 2–4 Ready',
    metricLabel: 'Indian market optimized',
    tag: 'Local',
  },
];

function FeatureCard({ feature, index }: { feature: typeof features[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateX = ((y - cy) / cy) * -6;
    const rotateY = ((x - cx) / cx) * 6;
    card.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(4px)`;
    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`spotlight-card shimmer-card glow-border animate-on-scroll stagger-${Math.min(index + 2, 6)} ${feature.colSpan} bg-white rounded-2xl p-8 border border-[rgba(15,23,42,0.07)] shadow-card group transition-all duration-200 relative overflow-hidden flex flex-col justify-between min-h-[240px]`}
      style={{ willChange: 'transform', transformStyle: 'preserve-3d' }}
    >
      {/* Number */}
      <span className="absolute top-6 right-6 font-mono-custom text-[11px] text-[#64748B] font-medium tracking-widest">{feature.number}</span>

      {/* Animated bg glow on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
        style={{ background: 'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(0, 102, 255,0.04) 0%, transparent 60%)' }} />

      {/* Icon */}
      <div className="flex items-start justify-between mb-6">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(0, 102, 255,0.3)] transition-all duration-500"
          style={{ background: 'linear-gradient(135deg, rgba(0, 102, 255,0.12), rgba(7, 93, 223,0.08))', border: '1px solid rgba(0, 102, 255,0.15)' }}
        >
          <Icon name={feature.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0066FF]" />
        </div>
        {feature.tag && (
          <span className="text-[10px] font-semibold tracking-widest uppercase text-[#0066FF] bg-[rgba(0, 102, 255,0.07)] px-2 py-1 rounded-full border border-[rgba(0, 102, 255,0.15)] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {feature.tag}
          </span>
        )}
      </div>

      <div className="flex-1">
        <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-3 leading-snug group-hover:text-[#0066FF] transition-colors duration-300">{feature.title}</h3>
        <p className="text-sm text-[#475569] leading-relaxed">{feature.description}</p>
      </div>

      {/* Metric with animated bar */}
      <div className="mt-6 pt-4 border-t border-[rgba(15,23,42,0.06)]">
        <div className="font-display text-2xl font-semibold text-gradient">{feature.metric}</div>
        <div className="text-[11px] text-[#64748B] font-medium mt-0.5 mb-2">{feature.metricLabel}</div>
        <div className="h-0.5 bg-[rgba(15,23,42,0.06)] rounded-full overflow-hidden">
          <div className="h-full w-0 group-hover:w-full transition-all duration-700 ease-out rounded-full"
            style={{ background: 'linear-gradient(90deg, #0066FF, #3B82F6)' }} />
        </div>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el) => el.classList.add('is-visible'));
          }
        });
      },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-[#FFFFFF] relative overflow-hidden">
      {/* Circuit background */}
      <div className="absolute inset-0 circuit-bg opacity-60 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="animate-on-scroll stagger-1 mb-16">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 max-w-[48px] bg-gradient-to-r from-[#0066FF] to-transparent" />
            <span className="section-label">What We Offer</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight leading-tight">
            Complete Digital Infrastructure for{' '}
            <span className="italic font-light gradient-text-animated">Ambitious Local Operators.</span>
          </h2>
          <p className="mt-4 text-lg text-[#475569] max-w-3xl leading-relaxed">
            Movio delivers everything local mobility and commerce businesses need to launch, operate, and scale—powered by enterprise-grade technology, built for real-world operations.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, i) => (
            <FeatureCard key={feature.id} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}